/**
 * Pricing API Service
 *
 * MVP 1.22 - Experience Pricing & Promotions Engine
 * Story: SCRUM-372 - View Experience Pricing
 *
 * Provides API methods for:
 * - Real-time pricing calculation
 * - Promo code validation
 * - Add-on fetching
 * - Active promotions
 */

import apiRequest from './api';

/**
 * Calculate customer pricing with full breakdown.
 * Returns real-time pricing based on date, party size, and add-ons.
 *
 * @param {string} experienceId - Experience UUID
 * @param {Object} options - Pricing options
 * @param {string} [options.date] - Trip date (ISO string)
 * @param {number} [options.partySize] - Number of participants (default: 2)
 * @param {Array<string>} [options.addonIds] - Selected add-on UUIDs
 * @param {string} [options.promoCode] - Promo code to apply
 * @returns {Promise<Object>} Pricing breakdown
 */
export async function calculatePricing(experienceId, options = {}) {
  const params = new URLSearchParams();

  if (options.date) {
    params.append('date', options.date);
  }

  if (options.partySize) {
    params.append('partySize', options.partySize.toString());
  }

  if (options.addonIds && options.addonIds.length > 0) {
    params.append('addonIds', options.addonIds.join(','));
  }

  if (options.promoCode) {
    params.append('promoCode', options.promoCode);
  }

  const queryString = params.toString();
  const endpoint = `/api/v1/experiences/${experienceId}/pricing/calculate${queryString ? `?${queryString}` : ''}`;

  return apiRequest(endpoint);
}

/**
 * Get simplified public pricing for an experience.
 * Used for search results and initial page load.
 *
 * @param {string} experienceId - Experience UUID
 * @param {number} [partySize] - Optional party size
 * @returns {Promise<Object>} Public pricing data
 */
export async function getPublicPricing(experienceId, partySize) {
  const endpoint = partySize
    ? `/api/v1/experiences/${experienceId}/pricing?partySize=${partySize}`
    : `/api/v1/experiences/${experienceId}/pricing`;

  return apiRequest(endpoint);
}

/**
 * Validate a promo code.
 *
 * @param {string} code - Promo code to validate
 * @param {string} experienceId - Experience UUID
 * @param {Object} [context] - Additional context
 * @param {number} [context.bookingValueCents] - Current booking value
 * @param {string} [context.tripDate] - Trip date (ISO string)
 * @returns {Promise<Object>} Validation result
 */
export async function validatePromoCode(code, experienceId, context = {}) {
  return apiRequest('/api/v1/pricing/validate-promo', {
    method: 'POST',
    body: JSON.stringify({
      code,
      experienceId,
      ...context
    })
  });
}

/**
 * Get available add-ons for an experience.
 *
 * @param {string} experienceId - Experience UUID
 * @returns {Promise<Object>} Add-ons list
 */
export async function getAddons(experienceId) {
  return apiRequest(`/api/v1/experiences/${experienceId}/addons`);
}

/**
 * Get active public promotions for an experience.
 *
 * @param {string} experienceId - Experience UUID
 * @returns {Promise<Object>} Active promotions list
 */
export async function getActivePromotions(experienceId) {
  return apiRequest(`/api/v1/experiences/${experienceId}/promotions/active`);
}

/**
 * Verify a quote and detect price changes.
 * Used during booking flow transition.
 * SCRUM-375: AC-004.2 through AC-004.6
 *
 * @param {string} quoteId - Quote ID from pricing calculation
 * @returns {Promise<Object>} Verification result with comparison
 */
export async function verifyQuote(quoteId) {
  return apiRequest('/api/v1/pricing/verify-quote', {
    method: 'POST',
    body: JSON.stringify({ quoteId })
  });
}

/**
 * Create a new quote for the booking flow.
 * Called when customer clicks "Book Now".
 *
 * @param {string} experienceId - Experience UUID
 * @param {Object} options - Pricing options
 * @returns {Promise<Object>} Quote with ID
 */
export async function createBookingQuote(experienceId, options = {}) {
  return apiRequest('/api/v1/pricing/create-booking-quote', {
    method: 'POST',
    body: JSON.stringify({
      experienceId,
      ...options
    })
  });
}

/**
 * Finalize a quote after successful checkout — sets is_final = true.
 * Fire-and-forget: failure should never block checkout success.
 * SCRUM-1311: Completes SCRUM-1280 AC-07.
 *
 * @param {string} quoteId - Quote ID from pricing calculation
 * @param {string} bookingId - Booking ID from checkout
 * @returns {Promise<Object>} Finalization result
 */
export async function finalizeQuote(quoteId, bookingId) {
  return apiRequest(`/api/v1/pricing/quotes/${quoteId}/finalize`, {
    method: 'POST',
    body: JSON.stringify({ bookingId })
  });
}

/**
 * Get pricing summary with tax for a booking.
 * Reads from immutable pricing snapshot — tax not recalculated.
 *
 * @param {string} bookingId - Booking UUID
 * @returns {Promise<Object>} Pricing summary with tax
 */
export async function getPricingSummary(bookingId) {
  return apiRequest(`/api/v1/bookings/${bookingId}/pricing-summary`);
}

export default {
  calculatePricing,
  getPublicPricing,
  validatePromoCode,
  getAddons,
  getActivePromotions,
  verifyQuote,
  createBookingQuote,
  finalizeQuote,
  getPricingSummary
};
